/**
 * AI Recruit - Idle Timeout Hook
 * Custom hook for handling user idle timeout and automatic logout
 */

import { useEffect, useRef, useCallback } from 'react'
import { useAuth } from './useAuth'
import { useSelector } from 'react-redux'
import type { RootState } from '../stores'

interface UseIdleTimeoutOptions {
  timeout: number // in milliseconds
  onIdle: () => void
  onActive?: () => void
  events?: readonly string[]
}

export const useIdleTimeout = ({
  timeout,
  onIdle,
  onActive,
  events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
}: UseIdleTimeoutOptions) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isIdleRef = useRef(false)
  const { isAuthenticated, logout } = useAuth()
  const { isLoading } = useSelector((state: RootState) => state.auth)

  const resetTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      if (isAuthenticated) {
        isIdleRef.current = true
        onIdle()
      }
    }, timeout)
  }, [timeout, onIdle, isAuthenticated])

  const handleActivity = useCallback(() => {
    if (isIdleRef.current) {
      isIdleRef.current = false
      onActive?.()
    }
    resetTimeout()
  }, [resetTimeout, onActive])

  useEffect(() => {
    if (!isAuthenticated || isLoading) {
      return
    }

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true)
    })

    // Start the timeout
    resetTimeout()

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true)
      })
    }
  }, [isAuthenticated, isLoading, events, handleActivity, resetTimeout])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return {
    resetTimeout,
    isIdle: isIdleRef.current
  }
}
